import { useState, useEffect } from "react";
import { MessageCircle, PhoneCall, MapPin } from "lucide-react";
import Modal from "../components/Modal";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import AnexoConversa from "../components/AnexoConversa";
import { SISTEMAS, BRAZILIAN_STATES } from "../constants";
import { generateId, getToday, maskPhone } from "../utils";
import { upsertRow } from "../lib/database";
import { lerRascunho, gravarRascunho, apagarRascunho } from "../lib/rascunho";
import { tipoDoRegistroRapido } from "../lib/rede";

export const RASCUNHO_QUICKFICHA = "registro-rapido";


const empty = {
  nomeLocal: "", contatoNome: "", telefone: "",
  cidade: "", estado: "SP", descricao: "", sistemas: ["manutencao"],
  // Como o contato foi feito — e a prova dele. Um registro rápido sem nenhuma
  // das duas coisas é uma empresa anotada, não um contato realizado.
  canal: "whatsapp", anexo: null, telefoneLigacao: "", quemAtendeu: "",
  // Visita presencial: o primeiro contato foi na porta da empresa, sem
  // mensagem antes — não há export nem ligação para mostrar.
  quemRecebeu: "",
};

export default function QuickFicha({ open, onClose, setCredenciamentos, userId, userName = "", role = "comercial" }) {
  const [data, setData] = useState({ ...empty });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const set = (patch) => setData((p) => ({ ...p, ...patch }));

  // Rascunho: o Android pode fechar o navegador no meio do registro (câmera,
  // troca de app) e a aba volta recarregada. Ao abrir, recupera o que havia.
  useEffect(() => {
    if (!open) return;
    const r = lerRascunho(RASCUNHO_QUICKFICHA);
    if (r) setData({ ...empty, ...r });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const temAlgo = data.nomeLocal || data.contatoNome || data.telefone || data.descricao || data.anexo || data.quemRecebeu || data.telefoneLigacao;
    if (temAlgo) gravarRascunho(RASCUNHO_QUICKFICHA, data);
  }, [open, data]);
  const toggleSis = (id) => setData((p) => ({ ...p, sistemas: p.sistemas.includes(id) ? p.sistemas.filter((x) => x !== id) : [...p.sistemas, id] }));
  const porTelefone = data.canal === "telefone";
  const porVisita = data.canal === "presencial";
  // Trocar o canal descarta o anexo: export e foto da visita não se substituem.
  const trocarCanal = (canal) => setData((p) => (p.canal === canal ? p : { ...p, canal, anexo: null }));

  const handleClose = () => { apagarRascunho(RASCUNHO_QUICKFICHA); setData({ ...empty }); setErrors({}); onClose(); };

  const handleSubmit = async () => {
    const err = {};
    if (!data.nomeLocal.trim() && !data.telefone.trim() && !data.contatoNome.trim())
      err.nomeLocal = "Informe ao menos o nome do local, contato ou telefone";
    // A prova do contato — o que faz este registro valer como contato feito.
    //
    // O registro rápido virou a porta de entrada de centenas de empresas, e
    // nada nele dizia se alguém tinha mesmo falado com elas: o card ia para "a
    // contatar" com a mesma cara de quem só foi anotado de um mapa. Agora são
    // dois caminhos, e os dois deixam rastro: o export da conversa (prova
    // forte, com hora e assinatura da plataforma) ou o registro telefônico, que
    // pede o número de onde a ligação saiu — não é prova, mas é responsabilidade
    // com nome e número, e aparece assim na trilha.
    if (porVisita) {
      // Visita presencial: a foto (fachada, cartão, com o responsável) é o
      // rastro, e o nome de quem recebeu diz com quem foi. Sem os dois, era
      // só "passei lá" — e o registro telefônico virava o atalho (Paradiesel).
      if (!data.anexo?.path || data.anexo.tipo !== "foto") {
        err.anexo = "Anexe uma foto da visita — fachada, cartão de visita ou com o responsável.";
      }
      if (!data.quemRecebeu.trim()) err.quemRecebeu = "Diga quem recebeu você na empresa.";
    } else if (porTelefone) {
      if ((data.telefoneLigacao || "").replace(/\D/g, "").length < 10) {
        err.telefoneLigacao = "Informe o número para onde você ligou (com DDD).";
      }
    } else if (!data.anexo?.path || data.anexo.tipo !== "conversa") {
      // Só o EXPORT: print não traz a conversa inteira nem hora verificável, e
      // não diz com quem foi. Ligação tem o caminho próprio (registro telefônico).
      err.anexo = data.anexo?.path
        ? "Print não vale: exporte a conversa na plataforma (botão \"export\") e anexe o arquivo. Se foi ligação, use \"Registro telefônico\"."
        : "Anexe o export da conversa — é o que comprova que o contato aconteceu.";
    }
    setErrors(err);
    if (Object.keys(err).length) return;

    const id = generateId();
    const today = getToday();
    const ts = new Date().toISOString();
    const res = data.anexo?.resumo || null;
    const telefoneContato = porVisita
      ? data.telefone.trim()
      : porTelefone
      ? data.telefoneLigacao.trim()
      : (data.telefone.trim() || res?.numero || "");

    // O contato entra na trilha da ficha, não em coluna nova: coluna que não
    // existe no banco derruba a linha inteira (ou engole o campo em silêncio).
    // É daqui que sai o selo "conversa validada" na Rede.
    const evento = porVisita
      ? {
          ts, acao: "contato", autor: userName || "", canal: "presencial",
          telefoneContato, quemAtendeu: data.quemRecebeu.trim(),
          // Foto prova a visita, mas não a conversa: sem selo "conversa validada"
          // (ele exige comprovanteTipo "conversa").
          comprovado: true,
          comprovantePath: data.anexo.path, comprovanteNome: data.anexo.nome,
          comprovanteTipo: "foto", contatoEm: ts,
          detalhe: `Visita presencial — recebido por ${data.quemRecebeu.trim()}. Foto da visita anexada.`,
        }
      : porTelefone
      ? {
          ts, acao: "contato", autor: userName || "", canal: "telefone",
          telefoneContato, quemAtendeu: data.quemAtendeu.trim() || "",
          comprovado: false,
          detalhe: `Contato por telefone para ${telefoneContato}${data.quemAtendeu.trim() ? ` — falou com ${data.quemAtendeu.trim()}` : ""}.`,
        }
      : {
          ts, acao: "contato", autor: userName || "", canal: "whatsapp",
          telefoneContato, comprovado: true,
          comprovantePath: data.anexo.path, comprovanteNome: data.anexo.nome,
          comprovanteTipo: data.anexo.tipo, conversaResumo: res,
          contatoEm: res?.contatoEm || ts,
          detalhe: res
            ? `Conversa exportada do WhatsApp — ${res.total} mensagens${res.numero ? ` com ${res.numero}` : ""}${res.assinado || res.todasAssinadas ? ", arquivo assinado pela plataforma" : ""}.`
            : "Print da conversa anexado.",
        };

    // Admin/gerente registrando = empresa para o time: nasce sem responsável,
    // e quem for trabalhar assume. Comercial que fez o contato fica com ela
    // (é o que impede duas pessoas de ligarem para a mesma empresa).
    const paraOTime = role === "admin" || role === "gerente";
    const record = {
      id, userId: paraOTime ? null : userId, origem: "contato", status: "pendente", situacao: "ATIVO",
      // O token nasce aqui, junto do id: é ele que faz o link enviado depois
      // reabrir ESTA ficha em vez de criar uma segunda (ver NewCredenciamento).
      editToken: generateId().replace(/-/g, ""),
      tipoEstabelecimento: tipoDoRegistroRapido(data.sistemas, data.nomeLocal), sistemas: data.sistemas,
      nomeFantasia: data.nomeLocal.trim() || null, razaoSocial: null,
      cidade: data.cidade.trim() || null, estado: data.estado || null, pais: "Brasil",
      telefone: data.telefone.trim() || telefoneContato || null,
      celular: porTelefone || porVisita ? null : telefoneContato || null,
      responsavelNome: data.contatoNome.trim() || (porVisita ? data.quemRecebeu.trim() : data.quemAtendeu.trim()) || null,
      especialidades: [], tiposVeiculo: [], documentos: [], perfil: {},
      notes: data.descricao.trim() || null,
      historico: [
        { ts, acao: "criacao", detalhe: `Registro rápido de contato${data.descricao.trim() ? ": " + data.descricao.trim() : ""}`, autor: userName || "" },
        evento,
      ],
      createdAt: today,
    };
    // Grava no banco e só fecha DEPOIS de confirmar — senão uma instabilidade
    // do servidor perderia o contato em silêncio. upsert por id = idempotente.
    setSaving(true);
    try {
      await upsertRow("credenciamentos", record, userId);
    } catch (e) {
      console.error("upsert registro rápido falhou:", e);
      setSaving(false);
      setErrors({ submit: "Não foi possível salvar agora (conexão ou servidor instável). Seus dados continuam aqui — tente de novo em instantes." });
      return;
    }
    setCredenciamentos((prev) => (prev.some((c) => c.id === id) ? prev.map((c) => (c.id === id ? record : c)) : [...prev, record]));
    setSaving(false);
    handleClose();
  };

  const chip = (on) => ({ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569", fontWeight: on ? 600 : 400 });
  const canalBtn = (on, cor) => ({
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: "9px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12,
    border: `1px solid ${on ? cor : "#e2e8f0"}`, background: on ? `${cor}12` : "#fff",
    color: on ? cor : "#475569", fontWeight: on ? 700 : 600,
  });

  return (
    <Modal open={open} onClose={handleClose} title="Registro rápido de contato" w={520}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14, lineHeight: 1.5 }}>
        Para quando você já falou com a empresa e ainda não tem o cadastro. Isso cria uma ficha <strong>pendente</strong> — depois é só clicar em <strong>Continuar</strong> para completar.
      </div>

      <Field label="Nome do local / estabelecimento" required error={errors.nomeLocal}>
        <input style={errors.nomeLocal ? inputErrorStyle : inputStyle} value={data.nomeLocal} onChange={(e) => set({ nomeLocal: e.target.value })} placeholder="Ex: Auto Center do João" />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="Nome do contato">
          <input style={inputStyle} value={data.contatoNome} onChange={(e) => set({ contatoNome: e.target.value })} placeholder="Quem atendeu" />
        </Field>
        <Field label="Telefone de contato">
          <input style={inputStyle} value={data.telefone} onChange={(e) => set({ telefone: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" />
        </Field>
        <Field label="Cidade">
          <input style={inputStyle} value={data.cidade} onChange={(e) => set({ cidade: e.target.value })} />
        </Field>
        <Field label="Estado (UF)">
          <select style={selectStyle} value={data.estado} onChange={(e) => set({ estado: e.target.value })}>
            {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf}</option>)}
          </select>
        </Field>
      </div>

      {/* ── Como o contato aconteceu ─────────────────────────────── */}
      <div style={{ marginTop: 6, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Como você falou com eles? <span style={{ color: "#dc2626" }}>*</span></div>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <button type="button" onClick={() => trocarCanal("whatsapp")} style={canalBtn(data.canal === "whatsapp", "#0d9488")}>
            <MessageCircle size={13} /> WhatsApp (export)
          </button>
          <button type="button" onClick={() => trocarCanal("telefone")} style={canalBtn(porTelefone, "#6366f1")}>
            <PhoneCall size={13} /> Telefone
          </button>
          <button type="button" onClick={() => trocarCanal("presencial")} style={canalBtn(porVisita, "#d97706")}>
            <MapPin size={13} /> Visita presencial
          </button>
        </div>

        {porVisita ? (
          <div>
            <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.45, marginBottom: 8 }}>
              Primeiro contato feito na empresa, sem mensagem antes. A foto e o nome de quem recebeu ficam na trilha com o seu nome. O card aparece <strong>sem o selo de conversa validada</strong> até um export chegar.
            </div>
            <Field label="Quem recebeu você" required error={errors.quemRecebeu}>
              <input style={errors.quemRecebeu ? inputErrorStyle : inputStyle} value={data.quemRecebeu} onChange={(e) => set({ quemRecebeu: e.target.value })} placeholder="Ex: Carlos, gerente da oficina" />
            </Field>
            <AnexoConversa
              valor={data.anexo}
              onChange={(v) => set({ anexo: v })}
              refId={data.nomeLocal.trim().slice(0, 20) || "registro-rapido"}
              obrigatorio
              erro={errors.anexo}
              modo="foto"
              titulo="Foto da visita"
              ajuda="Fachada da empresa, cartão de visita ou foto com o responsável. No celular, abre a câmera."
              onOcupado={setEnviandoAnexo}
            />
          </div>
        ) : porTelefone ? (
          <div style={{ padding: "11px 13px", borderRadius: 9, background: "#f8fafc", border: `1px solid ${errors.telefoneLigacao ? "#fecaca" : "#e2e8f0"}` }}>
            <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.45, marginBottom: 8 }}>
              Ligação não tem export. Diga o número que você chamou — fica na trilha com o seu nome, e o card aparece <strong>sem o selo de conversa validada</strong> até um export chegar.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
              <Field label="Número para onde ligou" required error={errors.telefoneLigacao}>
                <input style={errors.telefoneLigacao ? inputErrorStyle : inputStyle} value={data.telefoneLigacao} onChange={(e) => set({ telefoneLigacao: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" />
              </Field>
              <Field label="Quem atendeu">
                <input style={inputStyle} value={data.quemAtendeu} onChange={(e) => set({ quemAtendeu: e.target.value })} placeholder="Ex: recepção, o dono" />
              </Field>
            </div>
          </div>
        ) : (
          <AnexoConversa
            valor={data.anexo}
            onChange={(v) => setData((p) => {
              // O export já diz com quem foi a conversa do lado da empresa:
              // preenche nome e telefone que ainda estiverem vazios.
              const quem = v?.resumo?.contato && v.resumo.contato !== "—" ? v.resumo.contato : "";
              const num = v?.resumo?.numero ? maskPhone(String(v.resumo.numero).replace(/^55(?=\d{10,11}$)/, "")) : "";
              return { ...p, anexo: v, contatoNome: p.contatoNome || quem, telefone: p.telefone || num };
            })}
            refId={data.nomeLocal.trim().slice(0, 20) || "registro-rapido"}
            obrigatorio
            erro={errors.anexo}
            titulo="Export da conversa no WhatsApp"
            onOcupado={setEnviandoAnexo}
          />
        )}
      </div>

      <Field label="O que o cliente pediu / observação">
        <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={data.descricao} onChange={(e) => set({ descricao: e.target.value })} placeholder="Descreva o pedido, o serviço procurado, quem retornar contato, etc." />
      </Field>

      <Field label="Aparece nos sistemas">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SISTEMAS.map((s) => <button key={s.id} type="button" onClick={() => toggleSis(s.id)} style={chip(data.sistemas.includes(s.id))}>{s.label}</button>)}
        </div>
      </Field>

      {errors.submit && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 8, fontWeight: 500 }}>{errors.submit}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
        <button onClick={handleClose} disabled={saving} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.5 : 1 }}>Cancelar</button>
        <button onClick={handleSubmit} disabled={saving || enviandoAnexo} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 600, cursor: (saving || enviandoAnexo) ? "not-allowed" : "pointer", opacity: (saving || enviandoAnexo) ? 0.7 : 1 }}>
          {enviandoAnexo ? "Enviando anexo..." : saving ? "Salvando..." : "Salvar contato"}
        </button>
      </div>
    </Modal>
  );
}
